import { useState, useMemo } from 'react';
import { useApp } from '@/context/AppContext';
import { parseCSV, guessMapping, WAVEOPS_FIELDS } from '@/lib/csv';

const FIELD_OPTIONS = [{ key: '', label: '— ignore —' }, ...WAVEOPS_FIELDS];

export default function ImportCustomersModal() {
  const { addCustomer, closeModal, showToast } = useApp();
  const [step, setStep] = useState('upload');   // upload | map | importing | done
  const [fileName, setFileName] = useState('');
  const [parsed, setParsed] = useState({ headers: [], rows: [] });
  const [mapping, setMapping] = useState({});   // csvIndex -> tylers-fabricationKey
  const [imported, setImported] = useState(0);
  const [failed, setFailed] = useState([]);     // [{row, error}]

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    const text = await file.text();
    const result = parseCSV(text);
    if (result.headers.length === 0 || result.rows.length === 0) {
      showToast('Could not parse this CSV — is it empty?', false);
      return;
    }
    setParsed(result);
    setMapping(guessMapping(result.headers));
    setStep('map');
  };

  const nameMapped = useMemo(() => Object.values(mapping).includes('name'), [mapping]);

  const handleImport = async () => {
    setStep('importing');
    let ok = 0;
    const errors = [];
    for (let r = 0; r < parsed.rows.length; r++) {
      const row = parsed.rows[r];
      const cust = {};
      Object.entries(mapping).forEach(([idx, field]) => {
        if (!field) return;
        const value = (row[idx] || '').trim();
        if (!value) return;
        if (field === 'mrr') {
          const n = Number(value.replace(/[$,]/g, ''));
          if (!isNaN(n)) cust.mrr = n;
        } else if (field === 'gate_code') {
          cust.gateCode = value;
        } else if (field === 'pool_gal') {
          cust.poolGal = value;
        } else {
          cust[field] = value;
        }
      });
      if (!cust.name) {
        errors.push({ row: r + 2, error: 'Missing name (required)' }); // +2 because row 1 is header
        continue;
      }
      try {
        await addCustomer(cust);
        ok++;
      } catch (e) {
        errors.push({ row: r + 2, error: String(e?.message || e) });
      }
    }
    setImported(ok);
    setFailed(errors);
    setStep('done');
  };

  return (
    <div className="modal" style={{ maxWidth: 720, width: '95%' }}>
      <div className="modal-head">
        <span className="modal-title">⬆ Import Customers from CSV</span>
        <button className="btn btn-ghost btn-sm" onClick={closeModal}>✕</button>
      </div>
      <div className="modal-body">
        {step === 'upload' && (
          <div style={{ display: 'grid', gap: 14 }}>
            <div style={{ fontSize: 13, color: 'var(--t2)', lineHeight: 1.55 }}>
              Export your customer list from <strong>QuickBooks Customer Center</strong> as CSV (Excel-compatible), then upload it here.
              We'll auto-map common columns (Customer, Bill To, Phone, Email) — you can adjust before importing.
            </div>
            <input
              type="file"
              accept=".csv,text/csv,text/plain"
              onChange={handleFile}
              style={{ padding: 8, background: 'var(--s2)', borderRadius: 6 }}
            />
            <div style={{ fontSize: 11, color: 'var(--t3)' }}>
              Supports QuickBooks Online and Desktop exports. UTF-8 and Windows-1252 both work.
            </div>
          </div>
        )}

        {step === 'map' && (
          <div style={{ display: 'grid', gap: 12 }}>
            <div style={{ fontSize: 12, color: 'var(--t3)' }}>
              <strong style={{ color: 'var(--t1)' }}>{fileName}</strong> · {parsed.rows.length} customer{parsed.rows.length === 1 ? '' : 's'} found · map each column below:
            </div>
            <div style={{ maxHeight: 320, overflowY: 'auto', border: '1px solid var(--b1)', borderRadius: 6 }}>
              <table className="tbl" style={{ width: '100%', fontSize: 12 }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left', padding: '8px 10px' }}>CSV column</th>
                    <th style={{ textAlign: 'left', padding: '8px 10px' }}>Sample</th>
                    <th style={{ textAlign: 'left', padding: '8px 10px' }}>Map to →</th>
                  </tr>
                </thead>
                <tbody>
                  {parsed.headers.map((h, i) => {
                    const sample = (parsed.rows[0]?.[i] || '').slice(0, 40);
                    return (
                      <tr key={i}>
                        <td style={{ padding: '6px 10px', fontWeight: 600, color: 'var(--t1)' }}>{h || `(column ${i+1})`}</td>
                        <td style={{ padding: '6px 10px', color: 'var(--t3)' }}>{sample || '—'}</td>
                        <td style={{ padding: '6px 10px' }}>
                          <select
                            value={mapping[i] || ''}
                            onChange={e => setMapping({ ...mapping, [i]: e.target.value || null })}
                            style={{ width: '100%', padding: '4px 6px', background: 'var(--s2)', color: 'var(--t1)', border: '1px solid var(--b2)', borderRadius: 4 }}
                          >
                            {FIELD_OPTIONS.map(opt => (
                              <option key={opt.key} value={opt.key}>{opt.label}</option>
                            ))}
                          </select>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {!nameMapped && (
              <div style={{ fontSize: 12, color: 'var(--rose)' }}>
                You must map at least one column to <strong>Name</strong> before importing.
              </div>
            )}
            <div className="modal-foot">
              <button className="btn btn-ghost" onClick={() => setStep('upload')}>Back</button>
              <button className="btn btn-primary" disabled={!nameMapped} onClick={handleImport}>
                Import {parsed.rows.length} customer{parsed.rows.length === 1 ? '' : 's'}
              </button>
            </div>
          </div>
        )}

        {step === 'importing' && (
          <div style={{ fontSize: 13, color: 'var(--t2)', textAlign: 'center', padding: 30 }}>
            Importing… please don't close this dialog.
          </div>
        )}

        {step === 'done' && (
          <div style={{ display: 'grid', gap: 12 }}>
            <div style={{ fontSize: 14, color: 'var(--teal)', fontWeight: 700 }}>
              ✓ Imported {imported} customer{imported === 1 ? '' : 's'}
            </div>
            {failed.length > 0 && (
              <>
                <div style={{ fontSize: 12, color: 'var(--rose)' }}>
                  {failed.length} row{failed.length === 1 ? '' : 's'} failed:
                </div>
                <div style={{ maxHeight: 200, overflowY: 'auto', background: 'var(--s2)', borderRadius: 6, padding: 10, fontSize: 11.5, color: 'var(--t3)' }}>
                  {failed.slice(0, 30).map((f, i) => (
                    <div key={i}>Row {f.row}: {f.error}</div>
                  ))}
                  {failed.length > 30 && <div>… and {failed.length - 30} more</div>}
                </div>
              </>
            )}
            <div className="modal-foot">
              <button className="btn btn-primary" onClick={closeModal}>Done</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
